import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export interface CustomerGroup {
  id: string;
  name: string;
  description: string;
  defaultPaymentTerms: string;
  defaultCreditLimit: number;
  defaultDiscountPercentage: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface CustomerGroupListQuery {
  pageNumber: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface CustomerGroupListResult {
  items: CustomerGroup[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateCustomerGroupRequest {
  name: string;
  description: string;
  defaultPaymentTerms: string;
  defaultCreditLimit: number;
  defaultDiscountPercentage: number;
  isDefault: boolean;
}

export interface UpdateCustomerGroupRequest {
  name: string;
  description: string;
  defaultPaymentTerms: string;
  defaultCreditLimit: number;
  defaultDiscountPercentage: number;
  isActive: boolean;
}

/**
 * Filter matrix for building/updating a group from matching customers
 * (call-center "create customer group by" flow). Every supplied criterion is
 * ANDed together at the database level; at least one is required.
 */
export interface CustomerGroupCriteria {
  /** Region (county) name, e.g. "Nairobi". */
  county?: string;
  /** Town substring, case-insensitive. */
  town?: string;
  /** Individual | Organization. */
  customerType?: string;
  isActive?: boolean;
}

export interface CreateCustomerGroupFromCriteriaRequest {
  name: string;
  description: string;
  defaultPaymentTerms: string;
  defaultCreditLimit: number;
  defaultDiscountPercentage: number;
  isDefault: boolean;
  criteria: CustomerGroupCriteria;
}

export interface CreateCustomerGroupFromCriteriaResult {
  group: CustomerGroup;
  /** Number of customers freshly assigned to the new group. */
  assignedCount: number;
}

export interface AssignCustomersByCriteriaResult {
  /** Number of customers freshly assigned (already-in-group rows are skipped). */
  assignedCount: number;
}

export const customerGroupsApi = {
  getAllGroups: async (): Promise<CustomerGroup[]> => {
    const { data } = await apiClient.get<ApiResponse<CustomerGroup[]>>(
      '/CustomerGroups'
    );
    return data.data;
  },

  getGroupsPaged: async (query: CustomerGroupListQuery): Promise<CustomerGroupListResult> => {
    const { data } = await apiClient.get<ApiResponse<CustomerGroupListResult>>(
      '/CustomerGroups/paged',
      { params: query }
    );
    return data.data;
  },

  getActiveGroups: async (): Promise<CustomerGroup[]> => {
    const { data } = await apiClient.get<ApiResponse<CustomerGroup[]>>(
      '/CustomerGroups/active'
    );
    return data.data;
  },

  getGroupById: async (id: string): Promise<CustomerGroup> => {
    const { data } = await apiClient.get<ApiResponse<CustomerGroup>>(
      `/CustomerGroups/${id}`
    );
    return data.data;
  },

  createGroup: async (
    payload: CreateCustomerGroupRequest
  ): Promise<CustomerGroup> => {
    const { data } = await apiClient.post<ApiResponse<CustomerGroup>>(
      '/CustomerGroups',
      payload
    );
    return data.data;
  },

  updateGroup: async (
    id: string,
    payload: UpdateCustomerGroupRequest
  ): Promise<CustomerGroup> => {
    const { data } = await apiClient.put<ApiResponse<CustomerGroup>>(
      `/CustomerGroups/${id}`,
      payload
    );
    return data.data;
  },

  deleteGroup: async (id: string): Promise<void> => {
    await apiClient.delete(`/CustomerGroups/${id}`);
  },

  /** Creates a customer group and bulk-assigns all criteria-matching customers in one go. */
  createGroupFromCriteria: async (
    payload: CreateCustomerGroupFromCriteriaRequest
  ): Promise<CreateCustomerGroupFromCriteriaResult> => {
    const { data } = await apiClient.post<ApiResponse<CreateCustomerGroupFromCriteriaResult>>(
      '/CustomerGroups/from-criteria',
      payload
    );
    return data.data;
  },

  /** Bulk-assigns all criteria-matching customers into an existing group (single DB UPDATE). */
  assignCustomersByCriteria: async (
    groupId: string,
    criteria: CustomerGroupCriteria
  ): Promise<AssignCustomersByCriteriaResult> => {
    const { data } = await apiClient.post<ApiResponse<AssignCustomersByCriteriaResult>>(
      `/CustomerGroups/${groupId}/assign-from-criteria`,
      criteria
    );
    return data.data;
  },
};